import { Share2, Shirt, MessageCircle, MoreHorizontal, Flag, Images, ExternalLink, UserPlus, UserMinus, ThumbsUp, User, Volume2, VolumeX, Trash2, Maximize2, Play, Pause } from 'lucide-react';
import { Link } from 'react-router-dom';
import wantLogo from '@/assets/want-w-icon.svg';
import { useState, useEffect, memo, useCallback, useRef } from 'react';
import { useVideoVisibility } from '@/hooks/useVideoVisibility';
import { useVideoMute } from '@/contexts/VideoMuteContext';
import { useSponsoredImpression } from '@/hooks/useSponsoredImpression';
import { useSocialProof } from '@/hooks/useSocialProof';
import { cn } from '@/lib/utils';
import { FeedPostData } from '@/types/polls';
import { ImageThumbsVoting } from '@/components/polls/voting';
import { CommentsSheet } from '@/components/comments/CommentsSheet';
import { CommentPreview } from '@/components/comments/CommentPreview';
import { ReportDialog } from '@/components/reports/ReportDialog';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { SaveButton } from '@/components/ui/SaveButton';
import { ShareSheet } from '@/components/share/ShareSheet';
import { ImageCarousel } from '@/components/ui/ImageCarousel';
import { ImageLightbox, useImageLightbox } from '@/components/ui/ImageLightbox';
import { Badge } from '@/components/ui/badge';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useComments } from '@/hooks/useComments';
import { useReports } from '@/hooks/useReports';
import { useAuth } from '@/hooks/useAuth';
import { useSaveImage } from '@/hooks/useSaveImage';
import { useEngagementGate } from '@/hooks/useEngagementGate';
import { usePollVotes } from '@/hooks/usePollVotes';
import { useUserFollow } from '@/hooks/useUserFollow';
import { PrivatePollBadge } from '@/components/polls/PrivatePollBadge';
import { FriendVoters } from '@/components/feed/FriendVoters';
import { triggerHaptic } from '@/hooks/useHaptics';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

/** Parse raw vote record into per-image thumbsUp/thumbsDown counts. */
function parseImageVotes(
  images: { id: string }[],
  rawVotes: Record<string, number>,
): Record<string, { thumbsUp: number; thumbsDown: number }> {
  const votes: Record<string, { thumbsUp: number; thumbsDown: number }> = {};
  images.forEach(img => {
    votes[img.id] = { thumbsUp: 0, thumbsDown: 0 };
  });

  Object.entries(rawVotes).forEach(([key, count]) => {
    if (key.includes(':')) {
      const lastColonIndex = key.lastIndexOf(':');
      const imgId = key.substring(0, lastColonIndex);
      const voteType = key.substring(lastColonIndex + 1);
      if (votes[imgId]) {
        if (voteType === 'up') votes[imgId].thumbsUp = count;
        if (voteType === 'down') votes[imgId].thumbsDown = count;
      }
    } else if (key === 'yes' || key === 'no') {
      if (images[0]) {
        if (key === 'yes') votes[images[0].id].thumbsUp = count;
        if (key === 'no') votes[images[0].id].thumbsDown = count;
      }
    }
  });
  return votes;
}

// Image overlay buttons for single images (rate/yes-no polls)
const ImageOverlayButtons = memo(function ImageOverlayButtons({ 
  imageUrl,
  isSaved,
  onToggleSave,
  isLoading,
  className 
}: { 
  imageUrl: string;
  isSaved: boolean;
  onToggleSave: () => void;
  isLoading: boolean;
  className?: string;
}) {
  return (
    <div className={cn("absolute top-3 right-3 flex flex-col items-center gap-2", className)}>
      <SaveButton
        saved={isSaved}
        onToggle={onToggleSave}
        size="md"
        variant="overlay"
        disabled={isLoading}
      />
      <Link
        to="/try-on"
        state={{ productImage: imageUrl, productName: 'Feed Item' }}
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded-full",
          "bg-black/40 backdrop-blur-md text-white",
          "border border-white/20 shadow-md",
          "transition-all duration-200",
          "active:scale-95"
        )}
        aria-label="Try on"
      >
        <Shirt className="w-5 h-5" />
      </Link>
    </div>
  );
});

// Re-export types for backward compatibility
export type { FeedPostData, FeedPostImage } from '@/types/polls';

interface FeedPostProps {
  post: FeedPostData;
  isFirstInFeed?: boolean;
  /** Priority loading for above-the-fold images */
  priority?: boolean;
}

function FeedPostComponent({ post, isFirstInFeed = false, priority = false }: FeedPostProps) {
  const { user } = useAuth();
  const { hasEngagedSaves } = useEngagementGate();
  const { reportPoll, isSubmitting: isReportingPoll } = useReports();
  const { comments, commentCount, latestComment, isLoading: isLoadingComments, isSubmitting: isSubmittingComment, addComment, deleteComment } = useComments(post.id);
  const { toggleSave, isSaved, checkSavedStatus, isLoading: isSaving } = useSaveImage();
  const { userVotes, submitVote, isVoting, getVoteForImage, legacyVotes } = usePollVotes({ pollId: post.id });
  const { isFollowing, isLoading: isFollowLoading, toggleFollow } = useUserFollow(post.author.id);
  
  // Global video mute state - shared across all video polls
  const { isMuted: videoMuted, toggleMute: toggleVideoMute } = useVideoMute();
  
  // Track impressions for sponsored polls
  const impressionRef = useSponsoredImpression(post.id, post.isSponsored || false);
  
  // Video visibility tracking for lazy loading
  const videoRef = useRef<HTMLVideoElement>(null);
  const { containerRef: videoContainerRef, isVisible: isVideoVisible } = useVideoVisibility({ 
    threshold: 0.5,
    rootMargin: '100px 0px' // Start loading slightly before visible
  });
  
  const queryClient = useQueryClient();
  
  const [showComments, setShowComments] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { lightboxProps, openLightbox } = useImageLightbox();
  
  // Control video playback based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !post.videoUrl) return;
    
    if (isVideoVisible) {
      // Video src is set reactively — wait until it has data before playing
      const tryPlay = () => {
        video.play().catch(() => {
          // Autoplay blocked — muted autoplay fallback
          video.muted = true;
          video.play().catch(() => {});
        });
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.addEventListener('loadeddata', tryPlay, { once: true });
        return () => video.removeEventListener('loadeddata', tryPlay);
      }
    } else {
      video.pause();
    }
  }, [isVideoVisible, post.videoUrl]);

  // Pause video when overlays (like CreatePollSheet) open
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !post.videoUrl) return;

    const handlePause = () => video.pause();
    window.addEventListener('pause-all-feed-videos', handlePause);
    return () => window.removeEventListener('pause-all-feed-videos', handlePause);
  }, [post.videoUrl]);
  
  // Per-image vote tracking.
  // Initialized from post.votes; re-synced when post.votes reference changes
  // using the "adjust state during render" pattern (no extra commit vs useEffect).
  const [imageVotes, setImageVotes] = useState<Record<string, { thumbsUp: number; thumbsDown: number }>>(
    () => parseImageVotes(post.images, post.votes as Record<string, number>)
  );
  const [prevPostVotes, setPrevPostVotes] = useState(post.votes);
  if (post.votes !== prevPostVotes) {
    setPrevPostVotes(post.votes);
    setImageVotes(parseImageVotes(post.images, post.votes as Record<string, number>));
  }


  const [localUserImageVotes, setLocalUserImageVotes] = useState<Record<string, 'up' | 'down' | null>>({});

  // Get user vote for a specific image (from hook or local state)
  // Also handles legacy yes/no votes - they apply to the first image
  const getUserVoteForImage = useCallback((imageId: string): 'up' | 'down' | null => {
    // First check local state (for optimistic updates)
    if (localUserImageVotes[imageId]) return localUserImageVotes[imageId];
    
    // Check if this is the first image and user has legacy yes/no vote
    if (post.images[0]?.id === imageId && legacyVotes.length > 0) {
      // Legacy yes = up, no = down
      if (legacyVotes.includes('yes')) return 'up';
      if (legacyVotes.includes('no')) return 'down';
    }
    
    // Then check from hook (database) for new format votes
    return getVoteForImage(imageId);
  }, [localUserImageVotes, getVoteForImage, legacyVotes, post.images]);


  // If the feed payload has stale/missing vote counts, but the user has already voted,
  // fetch fresh counts so we don't show "Be the first to vote!" incorrectly.
  useEffect(() => {
    const rawVotes = post.votes as Record<string, number> | null | undefined;
    if (!user?.id) return;
    if (rawVotes && Object.keys(rawVotes).length > 0) return;

    const userHasVoted = post.images.some(img => getUserVoteForImage(img.id) !== null);
    if (!userHasVoted) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: freshPoll } = await supabase
          .from('polls')
          .select('votes')
          .eq('id', post.id)
          .maybeSingle();

        const freshVotes = freshPoll?.votes as Record<string, number> | null | undefined;
        if (cancelled || !freshVotes || Object.keys(freshVotes).length === 0) return;

        setImageVotes(parseImageVotes(post.images, freshVotes));
      } catch (err) {
        console.debug('[FeedPost] Fresh vote fetch failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, post.id, post.votes, post.images, getUserVoteForImage]);

  useEffect(() => {
    if (user && post.images.length > 0) {
      checkSavedStatus(post.images.map(img => img.url));
    }
  }, [user, post.images, checkSavedStatus]);

  const handleReportPoll = async (reason: 'spam' | 'harassment' | 'inappropriate' | 'other', description?: string) => {
    const success = await reportPoll(post.id, reason, description);
    if (success) {
      setShowReportDialog(false);
    }
  };

  const handleAuthRequired = () => {
    setShowAuthDialog(true);
  };

  // Delete poll handler
  const handleDeletePoll = useCallback(async () => {
    if (!user || user.id !== post.author.id) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('polls')
        .delete()
        .eq('id', post.id)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Deleted silently
      triggerHaptic('success');
      
      // Invalidate feed queries to refresh
      queryClient.invalidateQueries({ queryKey: ['feed-polls'] });
      queryClient.invalidateQueries({ queryKey: ['my-polls'] });
    } catch (error) {
      console.error('Failed to delete poll:', error);
      toast.error('Failed to delete poll');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [user, post.id, post.author.id, queryClient]);

  // Per-image voting handler - supports vote changes
  const handleImageVote = useCallback(async (imageId: string, voteType: 'up' | 'down') => {
    console.debug('[FeedPost] handleImageVote called', {
      pollId: post.id,
      imageId,
      voteType,
      hasUser: !!user,
      existingVote: getUserVoteForImage(imageId),
    });

    if (!user) {
      handleAuthRequired();
      return false;
    }
    
    const existingVote = getUserVoteForImage(imageId);
    
    // If clicking the same vote type, do nothing
    if (existingVote === voteType) return false;
    
    // Optimistically update local state
    const oldVoteType = existingVote;
    setLocalUserImageVotes(prev => ({ ...prev, [imageId]: voteType }));
    setImageVotes(prev => {
      const current = prev[imageId] || { thumbsUp: 0, thumbsDown: 0 };
      const updated = { ...current };
      
      // If changing vote, decrement old vote
      if (oldVoteType) {
        if (oldVoteType === 'up') updated.thumbsUp = Math.max(0, updated.thumbsUp - 1);
        if (oldVoteType === 'down') updated.thumbsDown = Math.max(0, updated.thumbsDown - 1);
      }
      
      // Increment new vote
      if (voteType === 'up') updated.thumbsUp += 1;
      if (voteType === 'down') updated.thumbsDown += 1;
      
      return { ...prev, [imageId]: updated };
    });
    
    // Submit vote as "imageId:up" or "imageId:down"
    const voteKey = `${imageId}:${voteType}`;
    const success = await submitVote(voteKey);

    console.debug('[FeedPost] submitVote result', {
      pollId: post.id,
      voteKey,
      success,
    });
    
    if (success) {
      // Fetch fresh vote counts from database to sync with other users' votes
      try {
        const { data: freshPoll } = await supabase
          .from('polls')
          .select('votes')
          .eq('id', post.id)
          .single();
        
        if (freshPoll?.votes) {
          setImageVotes(parseImageVotes(post.images, freshPoll.votes as Record<string, number>));
        }
      } catch (err) {
        console.debug('[FeedPost] Failed to fetch fresh votes:', err);
      }
    } else {
      // Revert on failure
      setLocalUserImageVotes(prev => ({ ...prev, [imageId]: oldVoteType }));
      setImageVotes(prev => {
        const current = prev[imageId] || { thumbsUp: 0, thumbsDown: 0 };
        const reverted = { ...current };
        
        // Revert: decrement new vote
        if (voteType === 'up') reverted.thumbsUp = Math.max(0, reverted.thumbsUp - 1);
        if (voteType === 'down') reverted.thumbsDown = Math.max(0, reverted.thumbsDown - 1);
        
        // Revert: increment old vote back
        if (oldVoteType) {
          if (oldVoteType === 'up') reverted.thumbsUp += 1;
          if (oldVoteType === 'down') reverted.thumbsDown += 1;
        }
        
        return { ...prev, [imageId]: reverted };
      });
    }
    
    return success;
  }, [user, getUserVoteForImage, submitVote, post.id, post.images]);

  const handleToggleSave = (imageUrl: string) => {
    if (!user) {
      handleAuthRequired();
      return;
    }
    toggleSave(imageUrl, post.caption || 'Saved from poll');
  };

  // Calculate total engagement for display (sum of all image votes)
  const realVotes = Object.values(imageVotes).reduce((sum, v) => 
    sum + (v?.thumbsUp || 0) + (v?.thumbsDown || 0), 0
  );

  // Auto-boost: deterministic jitter from poll ID, scaled by age
  const { autoBoostRate, autoBoostCap } = useSocialProof();
  const autoBoost = (() => {
    if (autoBoostRate <= 0 || realVotes === 0) return 0;
    // Deterministic jitter per poll (0.6 – 1.39)
    let hash = 0;
    for (let i = 0; i < post.id.length; i++) {
      hash = ((hash << 5) - hash) + post.id.charCodeAt(i);
      hash |= 0;
    }
    const jitter = 0.6 + (Math.abs(hash) % 80) / 100;
    const ageMs = Date.now() - new Date(post.createdAt).getTime();
    const ageHours = ageMs / (1000 * 60 * 60);
    return Math.min(Math.floor(autoBoostRate * ageHours * jitter), autoBoostCap);
  })();

  // Distribute boosted votes realistically across thumbs-up/down per image
  // Uses a deterministic "sentiment lean" (55-80% up) seeded from poll ID
  const boostedImageVotes = (() => {
    if (autoBoost <= 0) return imageVotes;

    // Deterministic lean per poll: 55% – 80% thumbs-up
    let hash2 = 0;
    for (let i = 0; i < post.id.length; i++) {
      hash2 = ((hash2 << 7) - hash2) + post.id.charCodeAt(i);
      hash2 |= 0;
    }
    const lean = 0.55 + (Math.abs(hash2) % 26) / 100; // 0.55 – 0.80

    // Distribute boost proportionally across images by their real vote share
    const imageIds = Object.keys(imageVotes);
    const totalReal = Math.max(1, realVotes);
    const result: Record<string, { thumbsUp: number; thumbsDown: number }> = {};

    imageIds.forEach(imgId => {
      const real = imageVotes[imgId] || { thumbsUp: 0, thumbsDown: 0 };
      const imgReal = real.thumbsUp + real.thumbsDown;
      // Proportional share of boost (equal split if no real votes yet)
      const imgBoost = realVotes === 0
        ? Math.floor(autoBoost / imageIds.length)
        : Math.round((imgReal / totalReal) * autoBoost);
      const boostUp = Math.round(imgBoost * lean);
      const boostDown = imgBoost - boostUp;
      result[imgId] = {
        thumbsUp: real.thumbsUp + boostUp,
        thumbsDown: real.thumbsDown + boostDown,
      };
    });

    return result;
  })();
  const totalVotes = realVotes + autoBoost;

  const currentImageUrl = post.images[currentImageIndex]?.url;

  const isOwnPost = user?.id === post.author.id;

  return (
    <>
      <article ref={impressionRef} className={cn("animate-fade-up", post.isOfficial && "border-t-2 border-[hsl(var(--primary))]")}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            {/* Official roadmap poll header */}
            {post.isOfficial ? (
              <div className="flex items-center gap-2.5">
                <img src={wantLogo} alt="WNTD" className="w-8 h-8" />
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground text-sm">WNTD Team</span>
                  <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold uppercase tracking-wide">
                    Roadmap
                  </span>
                  <span className="text-caption text-xs">·</span>
                  <span className="text-caption text-xs">{post.timestamp}</span>
                </div>
              </div>
            ) : post.isSponsored ? (
              <div className="flex items-center gap-2">
                <div className="px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                  Sponsored
                </div>
                {post.sponsorName && (
                  <span className="text-sm font-medium text-foreground">
                    {post.sponsorName}
                  </span>
                )}
              </div>
            ) : post.author.id ? (
              <Link 
                to={`/user/${post.author.id}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                {post.author.avatar ? (
                  <img
                    src={post.author.avatar}
                    alt={post.author.username}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-white/10"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary/20 ring-2 ring-white/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground text-sm">
                    {post.author.username}
                  </span>
                  <span className="text-caption text-xs">·</span>
                  <span className="text-caption text-xs">{post.timestamp}</span>
                  {post.visibility === 'private' && (
                    <PrivatePollBadge />
                  )}
                </div>
              </Link>
            ) : (
              <div className="flex items-center gap-3">
                {post.author.avatar ? (
                  <img
                    src={post.author.avatar}
                    alt={post.author.username}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-white/10 bg-muted"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary/20 ring-2 ring-white/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-muted-foreground text-sm">
                    {post.author.username}
                  </span>
                  <span className="text-caption text-xs">·</span>
                  <span className="text-caption text-xs">{post.timestamp}</span>
                  {post.visibility === 'private' && (
                    <PrivatePollBadge />
                  )}
                </div>
              </div>
            )}
          </div>
          
          {!post.isSponsored && !post.isOfficial && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowShareSheet(true)}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </DropdownMenuItem>
                {isOwnPost && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
                {!isOwnPost && (
                  <>
                    {post.author.id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => {
                            if (!user) {
                              handleAuthRequired();
                              return;
                            }
                            toggleFollow();
                          }}
                          disabled={isFollowLoading}
                        >
                          {isFollowing ? (
                            <>
                              <UserMinus className="h-4 w-4 mr-2" />
                              Unfollow
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4 mr-2" />
                              Follow
                            </>
                          )}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowReportDialog(true)}>
                      <Flag className="h-4 w-4 mr-2" />
                      Report
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Caption with vote count */}
        <div className="px-5 pb-4">
          <p className="text-body text-lg leading-relaxed">
            "{post.caption}"
          </p>
        </div>

        {/* Video or Image with inline voting */}
        {post.images.length > 0 && (
          <div className="px-5">
            {post.videoUrl ? (
              // Video poll - lazy loaded, plays only when visible
              <div ref={videoContainerRef} className="relative rounded-2xl overflow-hidden bg-muted">
                <video
                  ref={videoRef}
                  src={isVideoVisible ? post.videoUrl : undefined}
                  poster={post.images[0]?.url}
                  className="w-full aspect-[3/4] object-cover"
                  muted={videoMuted}
                  loop
                  playsInline
                  preload={isVideoVisible ? 'auto' : 'none'}
                />
                {/* Video controls - top left */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  <button
                    onClick={() => {
                      const video = videoRef.current;
                      if (!video) return;
                      if (video.paused) {
                        video.play().catch(() => {});
                        setIsVideoPaused(false);
                      } else {
                        video.pause();
                        setIsVideoPaused(true);
                      }
                    }}
                    className={cn(
                      "w-10 h-10 rounded-full",
                      "bg-black/40 backdrop-blur-sm text-white",
                      "flex items-center justify-center",
                      "transition-all duration-200 active:scale-95"
                    )}
                    aria-label={isVideoPaused ? "Play" : "Pause"}
                  >
                    {isVideoPaused ? (
                      <Play className="w-5 h-5" />
                    ) : (
                      <Pause className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={toggleVideoMute}
                    className={cn(
                      "w-10 h-10 rounded-full",
                      "bg-black/40 backdrop-blur-sm text-white",
                      "flex items-center justify-center",
                      "transition-all duration-200 active:scale-95"
                    )}
                    aria-label={videoMuted ? "Unmute" : "Mute"}
                  >
                    {videoMuted ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Action buttons - top right */}
                {currentImageUrl && (
                  <ImageOverlayButtons 
                    imageUrl={currentImageUrl}
                    isSaved={isSaved(currentImageUrl)}
                    onToggleSave={() => handleToggleSave(currentImageUrl)}
                    isLoading={isSaving}
                  />
                )}
                
                {/* Thumbs voting - bottom center */}
                <div className="absolute bottom-0 inset-x-0 z-20 pointer-events-auto">
                   <ImageThumbsVoting
                    imageId={post.images[0]?.id || '0'}
                    votes={boostedImageVotes[post.images[0]?.id] || { thumbsUp: 0, thumbsDown: 0 }}
                    userVote={getUserVoteForImage(post.images[0]?.id || '0')}
                    onVote={handleImageVote}
                    disabled={isVoting}
                  />
                </div>
              </div>
            ) : post.images.length === 1 ? (
              // Single image with voting
              <div className="relative rounded-2xl overflow-hidden bg-muted">
                <img
                  src={post.images[0].url}
                  alt={post.caption || 'Poll image'}
                  className="w-full aspect-[3/4] object-cover"
                  loading={priority ? 'eager' : 'lazy'}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Expand button - top left */}
                <button
                  onClick={() => openLightbox(post.images[0].url, post.caption || 'Poll image')}
                  className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform z-10"
                  aria-label="View full screen"
                >
                  <Maximize2 className="w-4 h-4 text-white" />
                </button>
                
                {/* Action buttons - top right */}
                <ImageOverlayButtons 
                  imageUrl={post.images[0].url}
                  isSaved={isSaved(post.images[0].url)}
                  onToggleSave={() => handleToggleSave(post.images[0].url)}
                  isLoading={isSaving}
                />
                
                {/* Thumbs voting - bottom center */}
                <div className="absolute bottom-0 inset-x-0 z-20 pointer-events-auto">
                   <ImageThumbsVoting
                    imageId={post.images[0].id}
                    votes={boostedImageVotes[post.images[0].id] || { thumbsUp: 0, thumbsDown: 0 }}
                    userVote={getUserVoteForImage(post.images[0].id)}
                    onVote={handleImageVote}
                    disabled={isVoting}
                  />
                </div>
              </div>
            ) : (
              // Multi-image carousel - each image gets voting
              <ImageCarousel 
                images={post.images.map(img => img.url)} 
                alt={post.caption || 'Poll image'}
                onIndexChange={setCurrentImageIndex}
                priority={priority}
              >
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Multi-image indicator + expand */}
                <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                  <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
                    <Images className="w-3.5 h-3.5" />
                    <span>{currentImageIndex + 1}/{post.images.length}</span>
                  </div>
                  <button
                    onClick={() => {
                      const url = post.images[currentImageIndex]?.url;
                      if (url) openLightbox(url, post.caption || 'Poll image');
                    }}
                    className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform"
                    aria-label="View full screen"
                  >
                    <Maximize2 className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
                
                {/* Action buttons - top right */}
                {currentImageUrl && (
                  <ImageOverlayButtons 
                    imageUrl={currentImageUrl}
                    isSaved={isSaved(currentImageUrl)}
                    onToggleSave={() => handleToggleSave(currentImageUrl)}
                    isLoading={isSaving}
                  />
                )}
                
                {/* Thumbs voting - above carousel dots */}
                {post.images[currentImageIndex] && (
                  <div className="absolute bottom-6 inset-x-0 z-20 pointer-events-auto">
                     <ImageThumbsVoting
                      imageId={post.images[currentImageIndex].id}
                      votes={boostedImageVotes[post.images[currentImageIndex].id] || { thumbsUp: 0, thumbsDown: 0 }}
                      userVote={getUserVoteForImage(post.images[currentImageIndex].id)}
                      onVote={handleImageVote}
                      disabled={isVoting}
                    />
                  </div>
                )}
              </ImageCarousel>
            )}
          </div>
        )}

        {/* Vote count - below image */}
        <div className="px-5 pt-3">
          <div className="flex items-center gap-3">
           {totalVotes > 0 ? (() => {
              const imageCount = post.images.length;
              
              if (imageCount <= 1) {
                const allUp = Object.values(boostedImageVotes).reduce((sum, v) => sum + (v.thumbsUp || 0), 0);
                const allDown = Object.values(boostedImageVotes).reduce((sum, v) => sum + (v.thumbsDown || 0), 0);
                const total = allUp + allDown;
                const upPct = total > 0 ? Math.round((allUp / total) * 100) : 0;
                const downPct = total > 0 ? 100 - upPct : 0;
                return (
                  <div className="flex flex-col gap-1 w-full">
                    <div className="flex items-center gap-2 w-full">
                      <span className={cn(
                        "font-semibold tabular-nums",
                        downPct >= upPct ? "text-sm" : "text-xs"
                      )} style={{ color: 'hsl(0, 84%, 60%)' }}>
                        👎 {downPct}%
                      </span>
                      <div className="flex-1 h-2 rounded-full overflow-hidden bg-muted flex">
                        {downPct > 0 && (
                          <div
                            className="h-full transition-all duration-300"
                            style={{
                              width: `${downPct}%`,
                              backgroundColor: 'hsl(0, 84%, 60%)',
                              borderRadius: upPct === 0 ? '9999px' : '9999px 0 0 9999px',
                            }}
                          />
                        )}
                        {upPct > 0 && (
                          <div
                            className="h-full transition-all duration-300"
                            style={{
                              width: `${upPct}%`,
                              backgroundColor: 'hsl(142, 71%, 45%)',
                              borderRadius: downPct === 0 ? '9999px' : '0 9999px 9999px 0',
                            }}
                          />
                        )}
                      </div>
                      <span className={cn(
                        "font-semibold tabular-nums",
                        upPct >= downPct ? "text-sm" : "text-xs"
                      )} style={{ color: 'hsl(142, 71%, 45%)' }}>
                        👍 {upPct}%
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground text-center">
                      {total} {total === 1 ? 'vote' : 'votes'}
                    </span>
                  </div>
                );
              }
              
              const perImage = post.images.map((img, idx) => {
                const votes = boostedImageVotes[img.id];
                return { index: idx + 1, votes: votes?.thumbsUp || 0 };
              });
              const totalPositive = perImage.reduce((s, p) => s + p.votes, 0);
              
              if (imageCount === 2) {
                const pct1 = totalPositive > 0 ? Math.round((perImage[0].votes / totalPositive) * 100) : 50;
                const pct2 = totalPositive > 0 ? 100 - pct1 : 50;
                const leader = pct1 >= pct2 ? 1 : 2;
                return (
                  <div className="flex flex-col gap-1.5 w-full">
                    <div className="flex items-center justify-between text-xs">
                      <span className={cn(
                        "font-medium",
                        leader === 1 ? "text-primary" : "text-muted-foreground"
                      )}>
                        Image 1 · {pct1}%
                      </span>
                      <span className={cn(
                        "font-medium",
                        leader === 2 ? "text-accent-foreground" : "text-muted-foreground"
                      )}>
                        Image 2 · {pct2}%
                      </span>
                    </div>

                    <div className="flex-1 h-2 rounded-full overflow-hidden bg-muted flex">
                      {pct1 > 0 && (
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${pct1}%`, borderRadius: pct2 === 0 ? '9999px' : '9999px 0 0 9999px' }}
                        />
                      )}
                      {pct2 > 0 && (
                        <div
                          className="h-full bg-accent transition-all duration-300"
                          style={{ width: `${pct2}%`, borderRadius: pct1 === 0 ? '9999px' : '0 9999px 9999px 0' }}
                        />
                      )}
                    </div>

                    <span className="text-xs text-muted-foreground text-center">
                      Image {leader} is leading · {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                    </span>
                  </div>
                );
              }
              
              const leader = perImage.reduce((best, cur) => cur.votes > best.votes ? cur : best, perImage[0]);
              const leaderPct = totalPositive > 0 ? Math.round((leader.votes / totalPositive) * 100) : 0;
              const colors = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(262, 80%, 60%)', 'hsl(38, 92%, 50%)', 'hsl(190, 80%, 45%)', 'hsl(340, 75%, 55%)'];
              return (
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-primary">#{leader.index} leading · {leaderPct}%</span>
                    <span className="text-xs text-muted-foreground">{totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}</span>
                  </div>
                  <div className="flex h-1.5 rounded-full overflow-hidden bg-muted">
                    {perImage.map((p, i) => {
                      const w = totalPositive > 0 ? (p.votes / totalPositive) * 100 : 100 / imageCount;
                      return <div key={i} className="h-full" style={{ width: `${w}%`, backgroundColor: colors[i % colors.length] }} />;
                    })}
                  </div>
                </div>
              );
            })() : (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <ThumbsUp className="w-3 h-3" />
                Be the first to vote!
              </span>
            )}
          </div>
        </div>

        {/* Actions and comments */}
        <div className="px-5 py-4 space-y-3">
          {/* Sponsored CTA button */}
          {post.isSponsored && post.sponsorCtaUrl && (
            <a
              href={post.sponsorCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl",
                "bg-primary/10 text-primary border border-primary/20",
                "text-sm font-medium",
                "transition-all duration-200 hover:bg-primary/20 active:scale-[0.98]"
              )}
            >
              {post.sponsorCtaText || 'Learn More'}
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          
          {/* Comment preview or comment button */}
          {latestComment ? (
            <CommentPreview
              comment={latestComment}
              totalCount={commentCount}
              onClick={() => setShowComments(true)}
            />
          ) : (
            <button 
              onClick={() => setShowComments(true)}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors active:scale-[0.98]"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm">Add a comment...</span>
            </button>
          )}
        </div>
      </article>

      {/* Comments Sheet */}
      <CommentsSheet
        open={showComments}
        onOpenChange={setShowComments}
        pollId={post.id}
        onAuthRequired={handleAuthRequired}
        comments={comments}
        isLoading={isLoadingComments}
        isSubmitting={isSubmittingComment}
        addComment={addComment}
        deleteComment={deleteComment}
        commentCount={commentCount}
        gated={!!user && !hasEngagedSaves}
      />

      {/* Report Dialog */}
      <ReportDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        onSubmit={handleReportPoll}
        isSubmitting={isReportingPoll}
        contentType="poll"
      />

      {/* Auth Dialog */}
      <AuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
      />

      {/* Share Sheet */}
      <ShareSheet
        title={post.caption || 'Poll'}
        text={`Vote on this poll: "${post.caption}"`}
        imageUrl={post.images[0]?.url}
        images={post.images.map(img => img.url)}
        pollId={post.id}
        userName={post.author?.username || undefined}
        open={showShareSheet}
        onOpenChange={setShowShareSheet}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this poll?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The poll and all its votes will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePoll}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ImageLightbox {...lightboxProps} />
    </>
  );
}

// Memoize the component to prevent unnecessary re-renders in lists
export const FeedPost = memo(FeedPostComponent);
